// Unique ids for the shared form inputs, so a <label for> and aria-describedby can reference the
// inner control. Ids are stable for the component's lifetime and unique per page.
let nextFieldId = 0;

export function createFieldId(prefix: string): string {
  nextFieldId += 1;
  return `${prefix}-${nextFieldId}`;
}

/** Space-separated id list for aria-describedby; null when nothing is referenced (attribute removed). */
export function describedBy(...ids: (string | null | undefined | false)[]): string | null {
  const value = ids.filter(Boolean).join(' ');
  return value.length > 0 ? value : null;
}

/**
 * Settled "error is shown" flags for the shared inputs' aria-invalid / aria-describedby / error message.
 *
 * Reading the inner NgModel's validity straight from the template is not change-detection safe: the
 * element's attribute bindings are evaluated before NgModel/RequiredValidator run their ngOnChanges on the
 * same element, so validity flips inside the same pass and dev mode throws NG0100. The flags are therefore
 * recomputed in a microtask after every status change (or blur / input change) and the view is marked
 * for check, so all three bindings read one already-settled value.
 */
export class FieldErrorState {
  shown: boolean[] = [];
  private subscriptions: { unsubscribe(): void }[] = [];
  private destroyed = false;

  constructor(
    private readonly markForCheck: () => void,
    private readonly isShown: (model: NgModelLike) => boolean
  ) {}

  /** First model's flag (single-control inputs). */
  get first(): boolean {
    return this.shown[0] ?? false;
  }

  watch(models: NgModelLike[]): void {
    this.unsubscribe();
    this.models = models;
    this.subscriptions = models
      .map((model) => model?.statusChanges?.subscribe(() => this.schedule()))
      .filter(Boolean);
    this.schedule();
  }

  schedule(): void {
    Promise.resolve().then(() => {
      if (this.destroyed) return;
      const next = this.models.map((model) => this.isShown(model));
      if (next.length !== this.shown.length || next.some((value, i) => value !== this.shown[i])) {
        this.shown = next;
        this.markForCheck();
      }
    });
  }

  destroy(): void {
    this.destroyed = true;
    this.unsubscribe();
  }

  private models: NgModelLike[] = [];

  private unsubscribe(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.subscriptions = [];
  }
}

/** The part of NgModel the error state needs (kept structural so the helper has no Angular import). */
export interface NgModelLike {
  errors: { [key: string]: any } | null;
  touched: boolean | null;
  statusChanges: { subscribe(next: () => void): { unsubscribe(): void } } | null;
}
